import React, {
	useCallback,
	useContext,
	useState
} from "react";
import {
	Link,
	useParams
} from "react-router-dom";
import {
	Box,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	Button,
	Grid,
	Snackbar
} from "@mui/material";
import { capitalizeFirstLetter } from "../../utils/text";
import { formatParts } from "../../utils/orderParts";
import { DataContext } from "../../App";
import { StatusBox } from "../Shared/StatusBox/StatusBox";
import { setDocumentTitle } from "../../utils/page";
import UseFetchData from "../Shared/FetchData/FetchData";
import { BreadCrumb } from "../Shared/BreadCrumb/BreadCrumb";
import { LoadableContent } from "../Shared/LoadableContent/LoadableContent";
import {DataHistoryLastWarehouseUpdate} from "../Shared/DataHistoryLastWarehouseUpdate/DataHistoryLastWarehouseUpdate";
import {getUserPermissions} from "../../utils/permissions";
import {AlertBox} from "../Shared/Alert/Alert";

export default function ReturnDetail({ seller, userdata }) {

	const { id: return_id } = useParams();
	const context = useContext(DataContext);
	const { status, results, isLoading } = UseFetchData(
		useCallback(() => context.dataProvider.getSellerReturn(seller, return_id), [context.dataProvider, seller, return_id])
	);

	const [state, setState] = useState({
		snackbar: {
			open: false,
			message: "",
			severity: "success"
		},
	});

	const permissionMap = {
		"force_return_sync": "force_return_sync",
	};
	let permissions = getUserPermissions(userdata, permissionMap);


	const BackButton = () => (
		<Button variant="contained" component={Link} to="/returns">
			Go Back
		</Button>
	);

	const pageName = `Return Details: ${results?.order_number ?? ""}`;
	setDocumentTitle(pageName);

	const onClickForwardSyncReturn = () => {
		context.dataProvider.forwardSyncReturn(seller, results?.bezos_system_id).then(() => {
			setState(previousState => {
				return {
					...previousState,
					snackbar: {
						open: true,
						message: "Return successfully reprocessed from Bezos System",
						severity: "success",
					}
				};
			});
		}).catch(() => {
			setState(previousState => {
				return {
					...previousState,
					snackbar: {
						open: true,
						message: "Return reprocessing failed",
						severity: "error"
					}
				};
			});
		});
	};

	const onSnackBarHandleClose = () => {
		setState(previousState => {
			return {
				...previousState,
				snackbar: {
					open: false,
				},
			};
		});
	};

	const ReturnForwardSyncButton = () => {
		return (
			<Grid item xs="auto">
				<Button
					variant="outlined"
					onClick={onClickForwardSyncReturn}
					color="secondary"
				>
					Reprocess Return from Bezos System
				</Button>
			</Grid>
		);
	};

	const TitleHeading = () => (
		<Box mb={0}>
			<Grid container spacing={1}>
				<Grid item xs={12} container spacing={3} mt={0} mb={2}>
					<Grid item xs="auto">
						<Typography component="h1" variant="h5">
							{pageName}
						</Typography>
					</Grid>
					<Grid item xs="auto">
						<StatusBox status={results?.status ?? ""} statusType="order" />
					</Grid>
					<Grid item xs />
					{(permissions.has("force_return_sync")) &&
						<>
							<ReturnForwardSyncButton />
						</>
					}
				</Grid>
			</Grid>
		</Box>
	);

	const BodyHeading = () => (
		<Paper sx={{ p: 2, width: "100%", mb: 2 }} elevation={2}>
			<Typography component="h1" variant="h6" mb={1}>
				Summary
			</Typography>

			<Grid container spacing={1}>

				<Grid item xs={12} sm={12} md={6} xl={6} mt={0.5}>
					<Typography component="div">
						<Box component="span" sx={{ fontWeight: "bold"}}>Return ID: </Box>
						<Box component="span">{results.bezos_system_id}</Box>
					</Typography>
				</Grid>

				<Grid item xs={12} sm={12} md={6} xl={6}>
					<Typography component="div">
						<Box component="span" sx={{ fontWeight: "bold" }}>Status: </Box>
						<Box component="span">{capitalizeFirstLetter(results?.status ?? "" )}</Box>
					</Typography>
				</Grid>

				<Grid item xs={12} sm={12} md={6} xl={6} mt={0.5}>
					<Typography component="div">
						<Box component="span" sx={{ fontWeight: "bold"}}>Order Number: </Box>
						<Box component="span">{results.order_number}</Box>
					</Typography>
				</Grid>

				<Grid item xs={12} sm={12} md={6} xl={6} mt={0.5}>
					<Typography component="div">
						<Box component="span" sx={{ fontWeight: "bold"}}>Part: </Box>
						<Box component="span">{formatParts(results.part, results.number_of_parts)}</Box>
					</Typography>
				</Grid>

				<Grid item xs={12} sm={12} md={6} xl={6}>
					<Typography component="div">
						<Box component="span" sx={{ fontWeight: "bold" }}>Last Warehouse Update: </Box>
						<Box component="span">
							<DataHistoryLastWarehouseUpdate dataHistory={results.data_history} />
						</Box>
					</Typography>
				</Grid>
			</Grid>
		</Paper>
	);

	const ListItems = () => {
		if (Array.isArray(results?.items) && results?.items.length > 0) {
			return results?.items.map((s) => (
				<TableRow key={s?.sku}>
					<TableCell align="left">{s?.sku ?? ""}</TableCell>
					<TableCell align="left">{s?.name ?? ""}</TableCell>
					<TableCell align="left">{s?.quantity ?? ""}</TableCell>
					<TableCell align="left">{s?.return_reason ?? ""}</TableCell>
					<TableCell align="left">{s?.comments ?? ""}</TableCell>
				</TableRow>
			));
		} else {
			return (
				<TableRow>
					<TableCell colSpan={5} align="center">
						No items found
					</TableCell>
				</TableRow>
			);
		}
	};

	const ProductsTable = () => (
		<Paper sx={{ p: 2, width: "100%", mb: 2 }} elevation={2}>
			<Typography component="h1" variant="h6" mb={1}>
				Return Items
			</Typography>
			<TableContainer component={Paper}>
				<Table sx={{ minWidth: 650 }} aria-label="simple table">
					<TableHead>
						<TableRow>
							<TableCell align="left">SKU</TableCell>
							<TableCell align="left">Name</TableCell>
							<TableCell align="left">Quantity</TableCell>
							<TableCell align="left">Return Reason</TableCell>
							<TableCell align="left">Comments</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						<ListItems />
					</TableBody>
				</Table>
			</TableContainer>
		</Paper>
	);

	return (
		<LoadableContent isLoading={isLoading}>
			<Box>
				{status ? (
					<>
						<BreadCrumb
							breadcrumbs={[
								{ label: "Returns", url: "/returns" },
								{ label: results?.order_number }
							]}
						/>
						<TitleHeading />
						<Paper sx={{ p: 2, width: "100%" }} elevation={2}>
							<BodyHeading />
							<ProductsTable />
						</Paper>
					</>
				) : (
					<>
						<Typography mb={2}>Invalid Return ID</Typography>
						<BackButton />
					</>
				)}
			</Box>
			<Snackbar
				open={state.snackbar.open}
				autoHideDuration={16000}
				onClose={onSnackBarHandleClose}
			>
				<Box>
					<AlertBox severity={state.snackbar.severity} message={state.snackbar.message} />
				</Box>
			</Snackbar>
		</LoadableContent>
	);
}
